/**
 * Challenge card – core layout, container, and action buttons.
 * Composed by challengeCard.styles.ts.
 */

import { Platform } from 'react-native';
import { makeBoxShadow } from '../../shared/helpers/shadowStyles.helper';
import type { Theme } from '../../theme/getTheme';

export function getChallengeCardCoreStyles(theme: Theme): Record<string, object> {
  return {
    card: {
      backgroundColor: theme.background.cardElevated,
      borderRadius: 20,
      padding: 12,
      paddingHorizontal: 14,
      marginVertical: 4,
      borderWidth: 0,
      overflow: 'hidden',
      ...(Platform.OS === 'web'
        ? makeBoxShadow({
            shadowColor: theme.gray[900] ?? '#1A202C',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.08,
            shadowRadius: 8,
          })
        : {
            shadowColor: theme.gray[900] ?? '#1A202C',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.08,
            shadowRadius: 8,
            elevation: 2,
          }),
    },
    cardFinishedWin: {
      borderLeftWidth: 4,
      borderLeftColor: theme.success + '99',
    },
    cardFinishedLoss: {
      borderLeftWidth: 4,
      borderLeftColor: theme.error + '99',
    },
    cardFinishedDraw: {
      borderLeftWidth: 4,
      borderLeftColor: theme.gray[400] + '99',
    },
    cardYourMove: {
      borderLeftWidth: 4,
      borderLeftColor: theme.primary,
    },
    cardLive: {
      borderLeftWidth: 4,
      borderLeftColor: theme.error,
    },
    cardActive: {
      borderLeftWidth: 4,
      borderLeftColor: theme.success,
    },
    cardWaiting: {
      borderLeftWidth: 4,
      borderLeftColor: theme.gray[300],
    },
    headerTouchable: {
      alignSelf: 'stretch',
      ...(Platform.OS === 'web' && { cursor: 'pointer' as const }),
    },
    headerTouchablePressed: {
      opacity: 0.9,
    },
    compactHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 0,
    },
    expandedAnimatedOuter: {
      overflow: 'hidden' as const,
    },
    expandedContentWrap: {
      borderTopWidth: 0,
      paddingTop: 8,
      marginTop: 8,
    },
    expandedCardFooter: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 8,
      paddingTop: 6,
      borderTopWidth: 1,
      borderTopColor: theme.gray[200],
    },
    expandedCardFooterItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
    expandedCardFooterText: {
      fontSize: 12,
      color: theme.gray[600],
    },
    /** Scrollable matches area; maxHeight set in component via useWindowDimensions (e.g. 0.45 * height). */
    expandedScrollContent: {
      flexGrow: 0,
    },
    compactHeaderLeft: {
      flex: 1,
      marginRight: 8,
    },
    compactHeaderRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    compactLeague: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.black,
      marginBottom: 2,
      textAlign: 'left',
      writingDirection: 'ltr',
    },
    compactCollapsedHint: {
      fontSize: 11,
      color: theme.gray[600],
    },
    compactMatchesWrapper: {
      marginBottom: 12,
    },
    noMatchesContainer: {
      padding: 16,
      alignItems: 'center',
      justifyContent: 'center',
    },
    noMatchesText: {
      fontSize: 12,
      color: theme.gray[600],
      fontWeight: '500',
      marginBottom: 6,
    },
    noMatchesSubtext: {
      fontSize: 11,
      color: theme.gray[600],
      textAlign: 'center',
    },
    compactShowMore: {
      alignItems: 'center',
      paddingVertical: 8,
      paddingHorizontal: 12,
      backgroundColor: theme.background.card,
      borderRadius: 6,
    },
    compactShowMoreText: {
      fontSize: 12,
      color: theme.primary,
      fontWeight: '500',
    },
    compactFooter: {
      marginTop: 12,
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
    },
    compactActionButton: {
      backgroundColor: theme.primary,
      borderRadius: 6,
      paddingVertical: 8,
      paddingHorizontal: 20,
      alignItems: 'center',
      alignSelf: 'center',
      ...(Platform.OS === 'web'
        ? makeBoxShadow({
            shadowColor: theme.shadow,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 2,
          })
        : {
            shadowColor: theme.shadow,
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 2,
            elevation: 2,
          }),
    },
    compactActionButtonDisabled: {
      backgroundColor: theme.gray[400],
      ...(Platform.OS !== 'web' && { shadowOpacity: 0, elevation: 0 }),
    },
    compactActionText: {
      color: theme.onPrimary,
      fontSize: 12,
      fontWeight: '600',
    },
    compactActionTextDisabled: {
      color: theme.gray[200],
    },
    /** Destructive secondary action: outline so it does not compete with main CTA. */
    compactCancelButton: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: theme.error,
    },
    compactCancelText: {
      color: theme.error,
    },
    compactDeclineButton: {
      backgroundColor: theme.error,
    },
    /** White on red (light/normal); dark text on bright red (dark) — same as status badges. */
    compactDeclineText: {
      color: theme.textOnStatusBadge,
    },
    cardRemindRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 10,
      marginBottom: 4,
    },
    compactRemindBlock: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    cardRemindButton: {
      width: 36,
      height: 36,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.primary,
      borderWidth: 1.5,
      borderColor: theme.primary,
      ...(Platform.OS === 'web'
        ? makeBoxShadow({
            shadowColor: theme.primary,
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.35,
            shadowRadius: 3,
          })
        : {
            shadowColor: theme.primary,
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.3,
            shadowRadius: 3,
            elevation: 3,
          }),
    },
    cardRemindButtonDisabled: {
      opacity: 0.85,
    },
  };
}
